import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CircleDot, Plus } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import ListFilterBar from '../components/work/ListFilterBar';
import { PageFrame } from '../components/work/page-chrome';
import { BrandEmpty, ErrorAlert, ListEmpty, LoadingRows, PageTitle } from '../components/work/page-states';
import { titleLinkClass } from '../components/work/table-cells';
import { useDirectoryQuery } from '../components/work/use-directory-query';
import { useSession } from '../components/session-context';
import { apiErrorMessage, apiFetch, v1Path } from '../lib/api';
import { formatFriendlyDateOnly } from '../lib/dates';
import {
  filterReleases,
  RELEASE_DEFAULT_ORDER,
  RELEASE_SORT_OPTIONS,
  RELEASE_STATUSES,
  releaseStatusLabel,
  type ReleaseSlim,
} from '../lib/releases';

const RELEASE_CHIPS = ['status'] as const;

export default function Releases() {
  const { isOrgAdmin } = useSession();
  const filters = useDirectoryQuery(RELEASE_CHIPS, RELEASE_DEFAULT_ORDER);
  const [items, setItems] = useState<ReleaseSlim[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [tag, setTag] = useState('');
  const [branchName, setBranchName] = useState('');
  const [targetDate, setTargetDate] = useState('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<{ items: ReleaseSlim[] }>(v1Path('/releases'));
      setItems(res.items);
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to load releases'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const visible = useMemo(
    () =>
      filterReleases(items, {
        query: filters.query,
        statuses: filters.selected.status,
        orderBy: filters.orderBy,
      }),
    [filters.orderBy, filters.query, filters.selected.status, items],
  );

  async function onCreate(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (name.trim().length === 0) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch(v1Path('/releases'), {
        method: 'POST',
        body: JSON.stringify({
          name: name.trim(),
          tag: tag.trim() || null,
          branch_name: branchName.trim() || null,
          target_date: targetDate || null,
        }),
      });
      setName('');
      setTag('');
      setBranchName('');
      setTargetDate('');
      setCreating(false);
      await load();
    } catch (err) {
      setError(apiErrorMessage(err, 'Failed to create release'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageFrame>
      <div className="flex flex-1 flex-col gap-4">
        <PageTitle
          title="Releases"
          count={loading || error ? undefined : visible.length}
          action={
            isOrgAdmin ? (
              <Button type="button" size="sm" onClick={() => setCreating((open) => !open)}>
                <Plus className="h-4 w-4" aria-hidden />
                {creating ? 'Cancel' : 'New release'}
              </Button>
            ) : null
          }
        />

        <ListFilterBar
          query={filters.query}
          onQueryChange={(query) => filters.apply({ query })}
          searchPlaceholder="Search name, tag or branch..."
          searchAriaLabel="Search releases"
          chips={[
            {
              id: 'status',
              label: 'Status',
              icon: CircleDot,
              options: RELEASE_STATUSES.map((status) => ({ value: status, label: releaseStatusLabel(status) })),
              selected: filters.selected.status,
              onToggle: (value) => filters.toggleChip('status', value),
            },
          ]}
          sort={{
            value: filters.orderBy ?? RELEASE_DEFAULT_ORDER,
            options: [...RELEASE_SORT_OPTIONS],
            onChange: (value) => filters.apply({ orderBy: value }),
          }}
          onClear={filters.clear}
        />

        {error ? <ErrorAlert>{error}</ErrorAlert> : null}
        {creating ? (
          <form onSubmit={(event) => void onCreate(event)} className="grid gap-3 rounded-md border p-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="release-name">Name</Label>
              <Input id="release-name" value={name} onChange={(e) => setName(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="release-tag">Tag</Label>
              <Input id="release-tag" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="2.4" />
            </div>
            <div>
              <Label htmlFor="release-branch">Base branch</Label>
              <Input
                id="release-branch"
                value={branchName}
                onChange={(e) => setBranchName(e.target.value)}
                placeholder="release/2.4"
              />
            </div>
            <div>
              <Label htmlFor="release-date">Target date</Label>
              <Input id="release-date" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} />
            </div>
            <div className="flex items-end">
              <Button type="submit" size="sm" disabled={saving || name.trim().length === 0}>
                {saving ? 'Creating…' : 'Create draft'}
              </Button>
            </div>
          </form>
        ) : null}

        {loading ? (
          <LoadingRows columns={6} />
        ) : items.length === 0 ? (
          <BrandEmpty title="No releases yet." description="A release collects the work that ships together." />
        ) : visible.length === 0 ? (
          <ListEmpty
            title="No releases match"
            description="Clear filters if you expected matches."
            action={
              <Button type="button" variant="outline" size="sm" onClick={filters.clear}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <Table className="table-fixed">
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-0 w-[32%]">Name</TableHead>
                <TableHead className="hidden w-28 md:table-cell">Tag</TableHead>
                <TableHead className="w-28">Status</TableHead>
                <TableHead className="hidden w-44 xl:table-cell">Branch</TableHead>
                <TableHead className="hidden w-32 md:table-cell">Planned for</TableHead>
                <TableHead className="w-24 text-right">Things in it</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((release) => (
                <TableRow key={release.id}>
                  <TableCell className="min-w-0">
                    <div className="truncate">
                      <Link to={`/releases/${release.id}`} className={titleLinkClass}>
                        {release.name}
                      </Link>
                    </div>
                    <span className="mt-0.5 block font-mono text-[11px] text-muted-foreground md:hidden">
                      {release.tag ?? '—'}
                    </span>
                  </TableCell>
                  <TableCell className="hidden font-mono text-xs text-muted-foreground md:table-cell">
                    {release.tag ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs capitalize text-muted-foreground">
                    {releaseStatusLabel(release.status)}
                  </TableCell>
                  <TableCell className="hidden truncate font-mono text-xs text-muted-foreground xl:table-cell">
                    {release.branch_name ?? '—'}
                  </TableCell>
                  <TableCell className="hidden text-xs tabular-nums text-muted-foreground md:table-cell">
                    {release.target_date ? formatFriendlyDateOnly(release.target_date, new Date()) ?? release.target_date : 'No date'}
                  </TableCell>
                  <TableCell className="text-right text-xs tabular-nums text-muted-foreground">
                    {release.item_count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </PageFrame>
  );
}
